import {
  buildEditorExtensions,
  contentToEditorHtml,
  stripMarkdownEmptyParagraphs,
  collapseMarkdownNewlines,
  setClipboardFromSelection,
} from "@components/design-system/molecules/richTextFormatUtils";
import { usePimsExportFormat } from "@hooks";
import { stripNbsp } from "@src/utils";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Editor,
  EditorContent,
  Extension,
  markInputRule,
  markPasteRule,
  useEditor,
} from "@tiptap/react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  ReactNode,
} from "react";

import { DSRichTextToolbar as DefaultToolbar } from "./DSRichTextToolbar";

// TipTap's Editor type doesn't expose getMarkdown even when the Markdown
// extension is loaded. This helper checks at runtime before calling.
function getEditorMarkdown(editor: Editor): string {
  const e = editor as unknown as { getMarkdown?: () => string };
  if (typeof e.getMarkdown === "function") return e.getMarkdown();
  return editor.getHTML();
}

// Input/paste rules for ++underline++ syntax. The Underline extension
// (bundled in StarterKit) handles keyboard shortcuts and serialization
// but doesn't ship with input rules, so we add them here.
const underlineInputRules = Extension.create({
  name: "underlineInputRules",
  addInputRules() {
    const type = this.editor.schema.marks["underline"];
    if (!type) return [];
    return [
      markInputRule({ find: /(?:^|\s)(\+\+((?:[^+]+))\+\+)$/, type }),
    ];
  },
  addPasteRules() {
    const type = this.editor.schema.marks["underline"];
    if (!type) return [];
    return [
      markPasteRule({ find: /(?:^|\s)(\+\+((?:[^+]+))\+\+)/g, type }),
    ];
  },
});

export type DSRichTextareaSize = "sm" | "md";

// Context for sharing editor state between compound components
interface RichTextareaContextValue {
  editor: Editor | null;
  disabled: boolean;
  size: DSRichTextareaSize;
  error?: string;
  generatedId: string;
  dataTestId: string;
  bgClass?: string;
  borderClass?: string;
  toolbarBgClass?: string;
  toolbarBorderClass?: string;
  plainText?: boolean;
}

const RichTextareaContext =
  createContext<RichTextareaContextValue | null>(null);

const useRichTextareaContext = () => {
  const context = useContext(RichTextareaContext);
  if (!context) {
    throw new Error(
      "DSRichTextarea compound components must be used within DSRichTextarea",
    );
  }
  return context;
};

// Main component props
export interface DSRichTextareaProps {
  value?: string;
  onChange: (content: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: DSRichTextareaSize;
  error?: string;
  label?: string;
  dataTestId: string;
  children: ReactNode;
  /** Optional background class for the input (overrides default bg-input) */
  bgClass?: string;
  /** Optional border class for the input (overrides default border-input) */
  borderClass?: string;
  /** Optional background class for the toolbar (overrides default bg-secondary) */
  toolbarBgClass?: string;
  /** Optional border class for the toolbar (overrides default border-secondary) */
  toolbarBorderClass?: string;
  /** Show placeholder even when the editor is not focused (default: false) */
  showPlaceholderAlways?: boolean;
  /** Use plain text mode: disables formatting extensions (bold, lists, tables, etc.) */
  plainText?: boolean;
}

// Root component that creates editor and provides context
export const DSRichTextarea = ({
  value = "",
  onChange,
  placeholder = "",
  disabled = false,
  size = "sm",
  error,
  label,
  dataTestId,
  children,
  bgClass,
  borderClass,
  toolbarBgClass,
  toolbarBorderClass,
  showPlaceholderAlways = false,
  plainText = false,
}: DSRichTextareaProps) => {
  const generatedId = useId();
  const pimsFormat = usePimsExportFormat();
  const pimsFormatRef = useRef(pimsFormat);
  useEffect(() => {
    pimsFormatRef.current = pimsFormat;
  }, [pimsFormat]);
  const editorRef = useRef<Editor | null>(null);
  const prevValueRef = useRef<string>(value);
  // Flag to suppress onChange when setContent is called from external value updates.
  // Initialized to true because useEditor fires an initial onUpdate on mount
  // before the user has made any edits; without this guard the component would
  // emit a (possibly re-normalized) value that triggers a feedback loop.
  const isExternalUpdateRef = useRef(true);

  const editor = useEditor({
    extensions: buildEditorExtensions(plainText, [
      Placeholder.configure({
        placeholder,
        showOnlyCurrent: !showPlaceholderAlways,
      }),
      ...(plainText ? [] : [underlineInputRules]),
    ]),
    // Both modes load content as HTML to preserve blank lines.
    // Plain text escapes markdown chars; rich text renders them via marked.
    content: contentToEditorHtml(value, plainText),
    editable: !disabled,
    onUpdate: ({ editor }) => {
      // Skip onChange for external setContent calls to prevent
      // feedback loops when streaming content into the editor
      if (isExternalUpdateRef.current) {
        isExternalUpdateRef.current = false;
        return;
      }

      if (editor.isEmpty) {
        prevValueRef.current = "";
        onChange?.("");
        return;
      }

      // Both modes emit single \n between paragraphs. Plain text uses
      // getText(); rich text uses getMarkdown() with post-processing:
      // 1. strip &nbsp; markers for empty paragraphs
      // 2. stripNbsp for any remaining non-breaking spaces
      // 3. collapse \n\n back to \n
      const content = plainText
        ? stripNbsp(editor.getText({ blockSeparator: "\n" }))
        : collapseMarkdownNewlines(
            stripNbsp(
              stripMarkdownEmptyParagraphs(getEditorMarkdown(editor)),
            ),
          );
      prevValueRef.current = content;
      onChange?.(content);
    },
    onCreate: ({ editor }) => {
      editorRef.current = editor;
      // Dispatch a no-op transaction so the Placeholder plugin evaluates
      // its decorations immediately (needed for showPlaceholderAlways)
      if (showPlaceholderAlways) {
        requestAnimationFrame(() => {
          editor.view.dispatch(editor.state.tr);
        });
      }
    },
    editorProps: {
      attributes: {
        "data-testid": dataTestId,
        "data-intercom-target": dataTestId,
      },
      handleDOMEvents: {
        copy: (view, event) => {
          const { from, to } = view.state.selection;
          if (from === to) return false;
          setClipboardFromSelection(
            view,
            event,
            editorRef,
            from,
            to,
            pimsFormatRef.current,
          );
          event.preventDefault();
          return true;
        },
        cut: (view, event) => {
          const { from, to } = view.state.selection;
          if (from === to) return false;
          setClipboardFromSelection(
            view,
            event,
            editorRef,
            from,
            to,
            pimsFormatRef.current,
          );
          view.dispatch(view.state.tr.deleteSelection());
          event.preventDefault();
          return true;
        },
      },
      handlePaste: (_view, event) => {
        if (plainText) return false;
        const text = event.clipboardData?.getData("text/plain");
        // Check if text looks like markdown (has bold, italic, headers, lists)
        const looksLikeMarkdown =
          text &&
          /\*\*.*\*\*|\*[^*].*[^*]\*|^#{1,6}\s|^[-*]\s|^\d+\.\s/m.test(
            text,
          );

        if (looksLikeMarkdown && editorRef.current) {
          editorRef.current.commands.insertContent(text, {
            contentType: "markdown",
          });
          return true; // Handled - prevent default paste
        }
        return false; // Let default handling proceed
      },
    },
  });

  // Update editor content when value prop changes (but not on initial mount)
  useEffect(() => {
    if (editor && value !== prevValueRef.current) {
      prevValueRef.current = value;
      isExternalUpdateRef.current = true;
      editor.commands.setContent(
        contentToEditorHtml(value, plainText),
      );
    }
  }, [value, editor, plainText]);

  // Update editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  return (
    <RichTextareaContext.Provider
      value={{
        editor,
        disabled,
        size,
        error,
        generatedId,
        dataTestId,
        bgClass,
        borderClass,
        toolbarBgClass,
        toolbarBorderClass,
        plainText,
      }}
    >
      <div className="flex flex-col gap-xs w-full">
        {label && (
          <label
            htmlFor={generatedId}
            className="text-xs text-secondary font-body"
          >
            {label}
          </label>
        )}
        <div className="flex flex-col gap-xs w-full">{children}</div>
        {error && !disabled && (
          <span className="text-xs text-danger">{error}</span>
        )}

        <style>{`
          /* Tiptap editor styles using design system tokens */
          .ProseMirror-focused {
            outline: none;
          }

          .tiptap {
            outline: none;
            min-height: 100%;
            cursor: text;
          }

          .tiptap > * {
            cursor: text;
          }

          /* Make EditorContent wrapper fill height */
          .ProseMirror {
            min-height: 100%;
          }

          .tiptap p {
            margin: 0;
            color: var(--text-primary);
          }

          .tiptap p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            color: var(--text-placeholder);
            pointer-events: none;
            height: 0;
            float: left;
          }

          .tiptap h1,
          .tiptap h2,
          .tiptap h3 {
            font-family: var(--font-title);
            font-weight: 600;
            color: var(--text-primary);
            margin-top: var(--spacing-sm);
            margin-bottom: var(--spacing-xs);
            line-height: 1.3;
          }

          .tiptap h1:first-child,
          .tiptap h2:first-child,
          .tiptap h3:first-child {
            margin-top: 0;
          }

          .tiptap h1 {
            font-size: 1.5rem;
          }

          .tiptap h2 {
            font-size: 1.25rem;
          }

          .tiptap h3 {
            font-size: 1.125rem;
          }

          .tiptap strong {
            font-weight: 700;
            color: var(--text-primary);
          }

          .tiptap em {
            font-style: italic;
          }

          .tiptap u {
            text-decoration: underline;
          }

          .tiptap ul,
          .tiptap ol {
            padding-left: var(--spacing-lg);
            margin: var(--spacing-xs) 0;
            color: var(--text-primary);
          }

          .tiptap ul {
            list-style-type: disc;
          }

          .tiptap ol {
            list-style-type: decimal;
          }

          .tiptap li {
            margin: var(--spacing-xs) 0;
          }

          .tiptap li p {
            display: inline;
          }

          .tiptap blockquote {
            border-left: 3px solid var(--color-brand);
            padding-left: var(--spacing-md);
            margin: var(--spacing-md) 0;
            color: var(--text-secondary);
            font-style: italic;
          }

          .tiptap code {
            background-color: var(--bg-secondary);
            color: var(--text-primary);
            padding: 2px 6px;
            border-radius: var(--radius-minimal);
            font-family: "Courier New", monospace;
            font-size: 0.9em;
          }

          .tiptap pre {
            background-color: var(--bg-secondary);
            color: var(--text-primary);
            padding: var(--spacing-md);
            border-radius: var(--radius-standard);
            margin: var(--spacing-md) 0;
            overflow-x: auto;
          }

          .tiptap pre code {
            background: none;
            padding: 0;
          }

          .tiptap table {
            border-collapse: collapse;
            width: 100%;
            margin: var(--spacing-xs) 0;
          }

          .tiptap th,
          .tiptap td {
            border: 1px solid var(--border-secondary);
            padding: var(--spacing-xs) var(--spacing-sm);
            text-align: left;
            color: var(--text-primary);
          }

          .tiptap th {
            font-weight: 600;
            background-color: var(--bg-secondary);
          }

          .tiptap hr {
            border: none;
            border-top: 1px solid var(--border-secondary);
            margin: var(--spacing-md) 0;
          }

          /* Disabled state */
          .tiptap[contenteditable="false"] {
            cursor: not-allowed;
            opacity: 0.6;
          }
        `}</style>
      </div>
    </RichTextareaContext.Provider>
  );
};

// Toolbar subcomponent
interface ToolbarProps {
  children?: ReactNode;
  textareaTitle?: string;
  hideFormatting?: boolean;
}

const Toolbar = ({
  children,
  textareaTitle,
  hideFormatting,
}: ToolbarProps) => {
  const {
    editor,
    disabled,
    dataTestId,
    toolbarBgClass,
    toolbarBorderClass,
  } = useRichTextareaContext();

  if (children) {
    return <>{children}</>;
  }

  return (
    <DefaultToolbar
      editor={editor}
      disabled={disabled}
      textareaTitle={textareaTitle}
      bgClass={toolbarBgClass}
      borderClass={toolbarBorderClass}
      dataTestId={`${dataTestId}-toolbar`}
      hideFormatting={hideFormatting}
    />
  );
};

// Input subcomponent
interface InputProps {
  minHeight?: string;
  maxHeight?: string;
}

const Input = ({ minHeight = "120px", maxHeight }: InputProps) => {
  const {
    editor,
    disabled,
    size,
    error,
    bgClass,
    borderClass,
    plainText,
  } = useRichTextareaContext();

  const sizeStyles = {
    sm: "px-md py-3 text-sm",
    md: "px-md py-3 text-base",
  };

  // Use custom border class if provided, otherwise use default
  const customBorderClass =
    borderClass ||
    "border-input focus-within:border-brand focus-within:shadow-[0px_0px_10px_0px_var(--shadow-focus)] hover:shadow-[0px_0px_10px_0px_var(--shadow-focus)]";
  const customBgClass = bgClass || "bg-input";

  const shadowClass = plainText
    ? "shadow-none"
    : "shadow-[0px_0px_10px_0px_var(--shadow-primary)]";
  const disabledShadowClass = plainText
    ? "shadow-none"
    : "shadow-[0px_0px_10px_0px_var(--shadow-secondary)]";
  const stateStyles = disabled
    ? `opacity-80 cursor-not-allowed ${disabledShadowClass} text-placeholder border-2 ${customBorderClass}`
    : error
      ? `${shadowClass} border-2 border-danger focus-within:shadow-[0px_0px_10px_0px_var(--shadow-focus)] focus-within:border-danger`
      : `border-2 ${customBorderClass} ${shadowClass}`;

  return (
    <div
      className={`${sizeStyles[size]} rounded-standard ${stateStyles} ${customBgClass} font-body transition-all overflow-auto flex flex-col cursor-text`}
      style={{
        minHeight,
        maxHeight,
      }}
      onClick={() => {
        if (!disabled && editor) {
          editor.commands.focus();
        }
      }}
    >
      <EditorContent editor={editor} className="flex-1" />
    </div>
  );
};

// Attach subcomponents
DSRichTextarea.Toolbar = Toolbar;
DSRichTextarea.Input = Input;
